import { ChangeDetectorRef, Directive, ElementRef, Input, Renderer2, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroupDirective } from '@angular/forms';
import { ApxAccessibleControlDirective } from '../utils/directives/acessible-control.directive';

/**
 * This is a base class that can be used to provide common functionality to
 * directives which are attached to native elements such as <input>. It can add/remove
 * classes, in addition to fetching and setting the FormControl instance if it is
 * contained within a Reactive form.
 *
 * @example <caption>A native input element using apxInput directive which extends this class.</caption>
 * <apx-form-field label="username">
 *    <input apxInput type="text" formControlName="name" />
 * </apx-form-field>
 */
@Directive()
export abstract class ApxControlDirective extends ApxAccessibleControlDirective {
    form = inject(FormGroupDirective, { optional: true });
    cdr = inject(ChangeDetectorRef);
    elemRef = inject(ElementRef);
    renderer = inject(Renderer2);

    @Input() formControlName!: string;
    @Input() formControl!: AbstractControl | null;
    @Input() set isInvalid(isInvalid: boolean) {
        if (this.setInvalidStyling) {
            this.setInvalidStyling(isInvalid);
        }
    }
    @Input() set value(value: string) {
        if (this.writeManualValue) {
            this.writeManualValue(value);
        }
    }

    /**
     * The extending class will optionally set this to apply context specific styling for invalid state
     * @param isInvalid
     */
    setInvalidStyling?(isInvalid: boolean): void;

    /**
     * setValue should be implemented in the extending class
     * @param value
     */
    writeManualValue?(value: string): void;

    /**
     * Get the control this directive is associated with through a Reactive form
     * If the formControlName or formControl is not set, then this is not being used
     * in a Reactive form and is standalone, so therefore return null.
     * @returns the FormControl or null
     */
    getControl(): FormControl | null {
        const containingForm = this.form?.form;
        if (this.formControl) {
            return this.formControl as FormControl; // if using [formControl]
        } else if (this.formControlName) {
            return containingForm?.get(this.formControlName) as FormControl; // if using [formControlName]
        } else {
            return null;
        }
    }

    /**
     * Remove classes from the native element the directive is attached to
     * @param classes
     */
    protected removeClasses(classes: string[]) {
        const ele = this.elemRef.nativeElement;
        classes.forEach(clas => {
            this.renderer.removeClass(ele, clas);
        });
    }

    /**
     * Add classes from the native element the directive is attached to
     * @param classes
     */
    protected addClasses(classes: string[]) {
        const ele = this.elemRef.nativeElement;
        classes.forEach(clas => {
            this.renderer.addClass(ele, clas);
        });
    }
}
